/**
 * Gender toggle — swaps collection content between paired gender collections
 * (e.g. Men's / Women's) without a full page reload.
 *
 * On click the active option flips instantly (before the fetch), and a small
 * loader shows over the product grid while the target collection is fetched
 * (only on a cache miss — prefetched toggles swap instantly and skip it).
 *
 * Updates are surgical, to avoid the reflow/jump of rebuilding whole subtrees:
 *   - Banner: badge/heading/description patched in place, image src swapped on
 *     the existing <img> (its box never changes → no reflow). The banner's
 *     checks/icons/badge are section settings shared by both genders, so only
 *     title/description/image actually differ.
 *   - Product grid: the theme's own filter approach — replace `.facest-filters-
 *     section` and call Collection.init() — which reserves card height via
 *     aspect-ratio, so it doesn't jump.
 *   - Collection header: replaced only if present (normally hidden by the banner).
 *
 * The swap is content-based, so paired collections do NOT need to share a
 * template. This controller NEVER redirects or reloads the page: if a fetch
 * fails or a swap step throws, it logs and stays put (the user can click
 * again). Every step runs through runSafely() so a single failure — including
 * the theme's own Collection.init() throwing — can't fall through to a
 * navigation. The toggles are <button data-url> (not <a href>): the swap is
 * JS-only, with no no-JS fallback by design.
 *
 * Built as a document-level controller (not a web component) because the toggle
 * markup lives inside the grid section it replaces, and popstate is a
 * window-level concern. This mirrors the theme's own MinimogTheme.Collection.
 */
(function () {
  'use strict';

  const SELECTORS = {
    link: '.gender-toggle__link',
    toggle: '.gender-toggle',
    grid: '.facest-filters-section',
    banner: '.collection-new-banner',
    header: '.m-collection-page-header',
    section: '.shopify-section',
    bannerImage: '.collection-banner__image img',
    productContainer: '[data-product-container]',
    collectionGrid: '#CollectionProductGrid',
  };
  // Banner bits that actually differ between genders (dynamic-source bound).
  const BANNER_FIELDS = ['.banner__stars-wrapper span', '.collection-banner__left h1', '.banner__description'];
  const INSTANT_CLASS = 'gender-toggle-instant';
  const LOADING_CLASS = 'gender-toggle-loading';
  const LOADER_CLASS = 'gender-toggle-loader';
  const WARM_IMAGE_LIMIT = 24;
  const SPINNER_SVG =
    '<svg class="gender-toggle-loader__spinner" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">' +
    '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>' +
    '<path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>' +
    '</svg>';

  class GenderToggle {
    constructor() {
      this.cache = new Map();
      this.pending = new Map();
      this.navigationToken = 0;
      this.bound = false;
      this.settleTimer = null;
      this.stickyObserver = null;
      this.toolbarFlowHeight = 0;
      this.onClick = this.onClick.bind(this);
      this.onPrefetch = this.onPrefetch.bind(this);
      this.onPopState = this.onPopState.bind(this);
    }

    init() {
      if (this.bound) return;
      if (!document.querySelector(SELECTORS.link)) return;

      document.addEventListener('click', this.onClick);
      document.addEventListener('pointerover', this.onPrefetch);
      document.addEventListener('touchstart', this.onPrefetch, { passive: true });
      document.addEventListener('focusin', this.onPrefetch);
      window.addEventListener('popstate', this.onPopState);

      try {
        const state = Object.assign({}, history.state, { genderToggle: true });
        history.replaceState(state, '', window.location.href);
      } catch (e) {
        /* history may be unavailable in some embedded contexts */
      }

      this.bound = true;
      this.watchStickyToolbar();
    }

    // The theme swaps the collection toolbar from position:sticky to
    // position:fixed on scroll (mobile only). Fixed leaves the normal flow, so
    // everything below jumps up by the toolbar's height. Reserve that space
    // with a spacer while the toolbar is fixed, so nothing moves.
    watchStickyToolbar() {
      if (this.stickyObserver) {
        this.stickyObserver.disconnect();
        this.stickyObserver = null;
      }
      const toolbar = document.querySelector('.m-collection-toolbar');
      if (!toolbar || !toolbar.parentNode) return;

      const parent = toolbar.parentNode;
      const SPACER = 'gender-toggle-toolbar-spacer';

      const sync = () => {
        const isFixed = toolbar.classList.contains('m-collection-toolbar--sticky');
        let spacer = parent.querySelector(':scope > .' + SPACER);
        if (!isFixed) {
          // Remember the in-flow height to match the spacer to it once fixed.
          const h = toolbar.getBoundingClientRect().height;
          if (h) this.toolbarFlowHeight = h;
          if (spacer) spacer.remove();
          return;
        }
        if (!spacer) {
          spacer = document.createElement('div');
          spacer.className = SPACER;
          spacer.setAttribute('aria-hidden', 'true');
          parent.insertBefore(spacer, toolbar);
        }
        spacer.style.height = (this.toolbarFlowHeight || toolbar.getBoundingClientRect().height) + 'px';
      };

      this.stickyObserver = new MutationObserver(sync);
      this.stickyObserver.observe(toolbar, { attributes: true, attributeFilter: ['class'] });
      sync();
    }

    // The toggles are <button data-url> (kept working if ever <a href> too).
    getTargetHref(el) {
      return el.dataset.url || el.getAttribute('href');
    }

    // Resolve a same-origin, not-already-active target path from an event.
    targetPathFromEvent(event) {
      const link = event.target.closest(SELECTORS.link);
      if (!link) return null;
      const href = this.getTargetHref(link);
      if (!href || href === '#') return null;
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      if (url.pathname === window.location.pathname) return null;
      return url.pathname;
    }

    onPrefetch(event) {
      const path = this.targetPathFromEvent(event);
      if (!path) return;
      this.fetchPage(path).catch(() => {});
    }

    onClick(event) {
      const link = event.target.closest(SELECTORS.link);
      if (!link) return;
      const href = this.getTargetHref(link);
      if (!href || href === '#') return;
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return;

      // Already-active gender: no-op, but don't reload.
      if (url.pathname === window.location.pathname) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      // Flip the active option immediately — don't wait for the fetch.
      this.setActive(link);
      this.navigate(url.pathname, { push: true });
    }

    onPopState() {
      this.navigate(window.location.pathname, { push: false });
    }

    // Move the `active` class to the given link within its toggle group.
    setActive(link) {
      const group = link.closest(SELECTORS.toggle);
      if (!group) return;
      group.querySelectorAll(SELECTORS.link).forEach((a) => {
        a.classList.remove('active');
        if (a.hasAttribute('aria-selected')) a.setAttribute('aria-selected', 'false');
      });
      link.classList.add('active');
      if (link.hasAttribute('aria-selected')) link.setAttribute('aria-selected', 'true');
    }

    navigate(path, options) {
      const push = options && options.push;
      const navigationToken = ++this.navigationToken;
      if (this.settleTimer) {
        clearTimeout(this.settleTimer);
        this.settleTimer = null;
      }

      // Show the grid loader only when we actually have to wait on the network.
      if (!this.cache.has(path)) this.showGridLoader();

      this.fetchPage(path)
        .then((doc) => {
          // A newer click won while this request was in flight. Ignore this
          // response so a slow request cannot overwrite the latest choice.
          if (navigationToken !== this.navigationToken) return;

          // The swap is content-based (replaceWith `.facest-filters-section`),
          // so the sections' shopify-section wrapper ids do NOT need to match
          // (they differ across templates, e.g. an "All" collection on its own).
          const currentGrid = document.querySelector(SELECTORS.grid);
          const nextGrid = doc.querySelector(SELECTORS.grid);
          if (!currentGrid || !nextGrid) {
            // Nothing to swap into — bail out cleanly. NEVER redirect/reload.
            this.hideGridLoader();
            return;
          }

          // Surgical, top-to-bottom. All synchronous → a single reflow.
          // Each step is isolated so one failure can't abort the others —
          // and, critically, can never fall through to a full-page navigation.
          this.runSafely('updateBanner', () => this.updateBanner(doc));
          this.runSafely('updateHeader', () => this.updateHeader(doc));
          this.runSafely('updateGrid', () => this.updateGrid(doc));

          // The theme's Collection.init() can throw on some themes/apps; that
          // must not bubble up (it used to trigger the redirect fallback).
          this.runSafely('Collection.init', () => {
            if (
              window.MinimogTheme &&
              MinimogTheme.Collection &&
              typeof MinimogTheme.Collection.init === 'function'
            ) {
              MinimogTheme.Collection.init();
            }
          });

          // Re-attach the sticky-toolbar spacer observer to the new toolbar.
          this.runSafely('watchStickyToolbar', () => this.watchStickyToolbar());

          const newTitle = doc.querySelector('title');
          if (newTitle) document.title = newTitle.textContent;

          if (push) {
            try {
              history.pushState({ genderToggle: true }, '', path);
            } catch (e) {
              /* ignore */
            }
          }

          this.settleTimer = setTimeout(() => this.clearInstant(), 600);
          this.hideGridLoader();
        })
        .catch((error) => {
          if (navigationToken !== this.navigationToken) return;

          // Fetch/parse failed. Do NOT redirect — just reset so the user can
          // click again. Everything stays on the page, no reload.
          console.error('[gender-toggle] fetch failed, staying put:', error);
          this.hideGridLoader();
          this.clearInstant();
        });
    }

    // Run a swap step in isolation: an error is logged but never propagates,
    // so a single failing step can't abort the swap or trigger any navigation.
    runSafely(label, fn) {
      try {
        fn();
      } catch (error) {
        console.error('[gender-toggle] ' + label + ' failed (ignored):', error);
      }
    }

    /**
     * Banner — patch the fields that differ (title/description/badge) and swap
     * the image src on the existing <img>, so the box never changes → no reflow.
     * If banner visibility differs between the pair, fall back to a whole swap.
     */
    updateBanner(doc) {
      const current = document.querySelector(SELECTORS.banner);
      const next = doc.querySelector(SELECTORS.banner);

      if (!current || !next) {
        this.swapWholeSection(doc, SELECTORS.banner);
        return;
      }

      BANNER_FIELDS.forEach((selector) => {
        const currentEl = current.querySelector(selector);
        const nextEl = next.querySelector(selector);
        if (currentEl && nextEl) {
          if (currentEl.innerHTML !== nextEl.innerHTML) currentEl.innerHTML = nextEl.innerHTML;
        } else if (currentEl && !nextEl) {
          currentEl.innerHTML = '';
        }
      });

      this.swapImage(current.querySelector(SELECTORS.bannerImage), next.querySelector(SELECTORS.bannerImage));
    }

    /**
     * Grid — the theme's own filter path: replace `.facest-filters-section` and
     * re-init the Collection controller. Card height is reserved via aspect-ratio
     * so this doesn't jump. Instant class is added before insertion so the new
     * images (and the reconnect init() triggers) don't flash.
     */
    updateGrid(doc) {
      const current = document.querySelector(SELECTORS.grid);
      const next = doc.querySelector(SELECTORS.grid);
      if (!current || !next) return;

      // Preserve the sticky-toolbar visual state across the swap. The fetched
      // toolbar comes back un-stuck; without this, switching while scrolled on
      // mobile makes the auto-hidden sticky toolbar reappear and shove the grid
      // down (~the toolbar's height) — the "jump" seen on mobile.
      const STICKY_CLASSES = ['m-collection-toolbar--sticky', 'scroll-up', 'scroll-down'];
      const currentToolbar = current.querySelector('.m-collection-toolbar');
      const preserved = currentToolbar
        ? STICKY_CLASSES.filter((c) => currentToolbar.classList.contains(c))
        : [];

      next.classList.add(INSTANT_CLASS);
      current.replaceWith(next);

      if (preserved.length) {
        const newToolbar = next.querySelector('.m-collection-toolbar');
        if (newToolbar) newToolbar.classList.add.apply(newToolbar.classList, preserved);
      }
      // Collection.init() is called by navigate() right after this — not here
      // (avoids running the theme's heavy re-init twice per swap).
    }

    // Header is normally hidden by the banner; swap it only when present.
    updateHeader(doc) {
      const current = document.querySelector(SELECTORS.header);
      const next = doc.querySelector(SELECTORS.header);
      if (!current || !next) return;
      next.classList.add(INSTANT_CLASS);
      current.replaceWith(next);
    }

    // Swap an <img> src/srcset in place, preloading first to avoid a blank
    // frame. Images are already warmed on prefetch, so this is near-instant.
    swapImage(currentImg, nextImg) {
      if (!currentImg || !nextImg) return;
      const src = nextImg.getAttribute('src');
      if (!src || currentImg.getAttribute('src') === src) return;
      const srcset = nextImg.getAttribute('srcset');

      const apply = () => {
        currentImg.setAttribute('src', src);
        if (srcset) currentImg.setAttribute('srcset', srcset);
      };

      const preload = new Image();
      preload.onload = apply;
      preload.onerror = apply;
      if (srcset) preload.srcset = srcset;
      preload.src = src;
    }

    // Fallback: replace a whole section by shopify-section id (used when a
    // section's visibility differs between the two genders).
    swapWholeSection(doc, contentSelector) {
      const id =
        this.resolveSectionId(document, contentSelector) ||
        this.resolveSectionId(doc, contentSelector);
      if (!id) return;
      const current = document.getElementById(id);
      const next = doc.getElementById(id);
      if (!current || !next) return;
      current.classList.add(INSTANT_CLASS);
      current.innerHTML = next.innerHTML;
    }

    // Small spinner over the product grid, on top of the (dimmed) current
    // products, while the next collection is being fetched.
    showGridLoader() {
      const host =
        document.querySelector(SELECTORS.productContainer) ||
        document.querySelector(SELECTORS.collectionGrid);
      if (!host || host.querySelector('.' + LOADER_CLASS)) return;

      host.classList.add(LOADING_CLASS);
      const loader = document.createElement('div');
      loader.className = LOADER_CLASS;
      loader.setAttribute('aria-hidden', 'true');
      loader.innerHTML = SPINNER_SVG;
      host.appendChild(loader);
    }

    hideGridLoader() {
      document.querySelectorAll('.' + LOADER_CLASS).forEach((el) => el.remove());
      document.querySelectorAll('.' + LOADING_CLASS).forEach((el) => el.classList.remove(LOADING_CLASS));
    }

    clearInstant() {
      document.querySelectorAll('.' + INSTANT_CLASS).forEach((el) => el.classList.remove(INSTANT_CLASS));
      this.settleTimer = null;
    }

    resolveSectionId(root, contentSelector) {
      const el = root.querySelector(contentSelector);
      const wrapper = el ? el.closest(SELECTORS.section) : null;
      return wrapper ? wrapper.id : null;
    }

    fetchPage(path) {
      // Cache immutable HTML, not the parsed Document. updateGrid() adopts the
      // target grid node into the live document, which removes it from its
      // original parsed Document. Reusing that Document made repeat visits to
      // a cached tab silently fail because it no longer contained a grid.
      if (this.cache.has(path)) return Promise.resolve(this.parsePage(this.cache.get(path)));
      if (this.pending.has(path)) {
        return this.pending.get(path).then((html) => this.parsePage(html));
      }

      const request = fetch(path)
        .then((response) => {
          if (!response.ok) throw new Error('Failed to fetch ' + path + ' (' + response.status + ')');
          return response.text();
        })
        .then((html) => {
          this.cache.set(path, html);
          this.pending.delete(path);
          const doc = this.parsePage(html);
          this.warmImages(doc);
          return html;
        })
        .catch((error) => {
          this.pending.delete(path);
          throw error;
        });

      this.pending.set(path, request);
      return request.then((html) => this.parsePage(html));
    }

    parsePage(html) {
      return new DOMParser().parseFromString(html, 'text/html');
    }

    // Warm the browser cache for the images we're about to show.
    warmImages(doc) {
      const images = doc.querySelectorAll(
        SELECTORS.banner + ' img, ' + SELECTORS.grid + ' responsive-image img'
      );
      let warmed = 0;
      images.forEach((img) => {
        if (warmed >= WARM_IMAGE_LIMIT) return;
        const src = img.getAttribute('src');
        if (!src) return;
        const warm = new Image();
        const srcset = img.getAttribute('srcset');
        const sizes = img.getAttribute('sizes');
        if (srcset) warm.srcset = srcset;
        if (sizes) warm.sizes = sizes;
        warm.src = src;
        warmed += 1;
      });
    }
  }

  window.MinimogTheme = window.MinimogTheme || {};
  if (!window.MinimogTheme.GenderToggle) {
    window.MinimogTheme.GenderToggle = new GenderToggle();
  }

  const gt = window.MinimogTheme.GenderToggle;

  const start = () => gt.init();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
